import type { CanvasSurface } from "./canvas";
import type { InputController } from "./input";
import type { GameHud, GameInstance } from "./types";

const STEP_SECONDS = 1 / 60;
const MAX_FRAME_SECONDS = 0.1;

export class GameLoop {
  private game: GameInstance | null = null;
  private animationFrame = 0;
  private previousTime = 0;
  private accumulator = 0;
  private running = false;
  private paused = false;

  constructor(
    private readonly surface: CanvasSurface,
    private readonly input: InputController,
    private readonly onHud: (hud: GameHud) => void,
    private readonly onEnd: (hud: GameHud) => void,
  ) {}

  start(game: GameInstance): void {
    this.stop();
    this.game = game;
    this.running = true;
    this.paused = false;
    this.previousTime = performance.now();
    this.accumulator = 0;
    this.animationFrame = requestAnimationFrame(this.tick);
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.previousTime = performance.now();
    this.accumulator = 0;
    if (!this.running || !this.game) return;

    if (paused) {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
      const context = this.surface.prepare();
      this.game.render(context);
      this.drawPauseOverlay(context);
      this.onHud(this.game.getHud());
      return;
    }

    this.animationFrame = requestAnimationFrame(this.tick);
  }

  isPaused(): boolean {
    return this.paused;
  }

  stop(): void {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.running = false;
    this.paused = false;
    this.game?.destroy();
    this.game = null;
    this.input.endFrame();
  }

  private readonly tick = (now: number): void => {
    this.animationFrame = 0;
    if (!this.running || !this.game || this.paused) return;

    const wallElapsed = Math.max(0, (now - this.previousTime) / 1000);
    const simulationElapsed = Math.min(wallElapsed, MAX_FRAME_SECONDS);
    this.previousTime = now;
    this.game.advanceTime(wallElapsed);

    let updated = false;
    this.accumulator += simulationElapsed;
    while (this.accumulator >= STEP_SECONDS) {
      this.game.update(STEP_SECONDS);
      this.accumulator -= STEP_SECONDS;
      updated = true;
    }

    const context = this.surface.prepare();
    this.game.render(context);

    const hud = this.game.getHud();
    this.onHud(hud);
    if (updated) this.input.endFrame();

    if (hud.ended) {
      this.running = false;
      this.onEnd(hud);
      return;
    }

    this.animationFrame = requestAnimationFrame(this.tick);
  };

  private drawPauseOverlay(context: CanvasRenderingContext2D): void {
    context.save();
    context.fillStyle = "rgba(5, 9, 20, 0.72)";
    context.fillRect(0, 0, 1280, 720);
    context.fillStyle = "#f7fbff";
    context.textAlign = "center";
    context.font = '900 52px "Avenir Next", sans-serif';
    context.fillText("PAUSED", 640, 342);
    context.fillStyle = "#9feddd";
    context.font = '700 18px "Avenir Next", sans-serif';
    context.fillText("Resume from the control bar", 640, 382);
    context.restore();
  }
}
